/**
 * GA4 helpers for coinproof.ai (Vue SPA).
 *
 * Loads gtag only when VUE_APP_ANALYTICS_ID is set (G-XXXXXXXX).
 * No-ops when empty so laptop work stays quiet.
 *
 * @see ../../../../ops/ga4-vue-setup.md
 */

import { analyticsId } from '@/config';

const PURCHASE_SENT_PREFIX = 'coinproof_ga4_purchase_';

function hasId() {
  return Boolean(analyticsId && /^G-[A-Z0-9]+$/i.test(analyticsId));
}

/**
 * Inject gtag.js once. Call from main.js before mount.
 */
export function initAnalytics() {
  if (typeof window === 'undefined' || !hasId()) return;
  if (window.__coinproofGa4Ready) return;

  const id = analyticsId;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', id, {
    send_page_view: false,
    anonymize_ip: true,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  window.__coinproofGa4Ready = true;
}

/**
 * SPA page view (call on every vue-router navigation).
 * @param {string} path
 * @param {string} [title]
 */
export function trackPageView(path, title) {
  if (!hasId() || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path || window.location.pathname,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

/**
 * Generic event.
 * @param {string} name
 * @param {Record<string, string | number | boolean | undefined>} [params]
 */
export function trackEvent(name, params = {}) {
  if (!hasId() || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const clean = {};
  Object.keys(params || {}).forEach((key) => {
    const v = params[key];
    if (v !== undefined && v !== null && v !== '') clean[key] = v;
  });
  window.gtag('event', name, clean);
}

/**
 * Landing / pricing CTA toward register.
 * @param {{ intent?: string, placement?: string }} opts
 */
export function trackCtaSignup({ intent, placement } = {}) {
  trackEvent('cta_signup', {
    intent: intent || 'trial',
    placement: placement || 'unknown',
  });
}

/**
 * Stripe Checkout Session about to open (upgrade / reactivate).
 * @param {{ interval?: 'month'|'year', value?: number }} opts
 */
export function trackCheckoutStart({ interval, value } = {}) {
  trackEvent('checkout_start', {
    interval: interval || undefined,
    value: typeof value === 'number' ? value : undefined,
    currency: 'USD',
  });
  trackEvent('begin_checkout', {
    currency: 'USD',
    value: typeof value === 'number' ? value : undefined,
    items: [
      {
        item_id: 'coinproof_pro',
        item_name: 'Coinproof Pro',
        item_variant: interval || undefined,
        price: typeof value === 'number' ? value : undefined,
        quantity: 1,
      },
    ],
  });
}

/**
 * Paid conversion (call once when you have a durable transaction id).
 * Deduped in sessionStorage.
 * @param {{ transactionId: string, value?: number, currency?: string, interval?: string }} opts
 */
export function trackPurchase(opts = {}) {
  const transactionId = opts.transactionId || '';
  if (!transactionId || typeof sessionStorage === 'undefined') return;

  const key = `${PURCHASE_SENT_PREFIX}${transactionId}`;
  try {
    if (sessionStorage.getItem(key) === '1') return;
  } catch {
    /* private mode */
  }

  const value = typeof opts.value === 'number' ? opts.value : undefined;
  const currency = (opts.currency || 'USD').toUpperCase();

  trackEvent('purchase', {
    transaction_id: transactionId,
    currency,
    value,
    items: [
      {
        item_id: 'coinproof_pro',
        item_name: 'Coinproof Pro',
        item_variant: opts.interval || undefined,
        price: value,
        quantity: 1,
      },
    ],
  });

  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

export function isAnalyticsEnabled() {
  return hasId();
}
